import React, { useEffect, useMemo, useState } from "react";
import {
  Bike,
  Car,
  ChevronDown,
  IndianRupee,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Truck,
} from "lucide-react";
import { useGlobalContext } from "../../GlobalContext";
import useChargesAndVehicles from "../../Sevices/useChargesAndVehicles";

const VEHICLE_IMAGES = {
  "2 Wheeler": "https://www.packky.com/images/Vechiles/two_wheeler.webp",
  "Two Wheeler": "https://www.packky.com/images/Vechiles/two_wheeler.webp",
  "3 Wheeler": "https://www.packky.com/images/Vechiles/three_wheeler.webp",
  "Three Wheeler": "https://www.packky.com/images/Vechiles/three_wheeler.webp",
  "4 Wheeler": "https://www.packky.com/images/Vechiles/four_wheeler.webp",
  "Four Wheeler": "https://www.packky.com/images/Vechiles/four_wheeler.webp",
  "Mini Truck": "https://www.packky.com/images/Vechiles/mini_truck.webp",
};

const getVehicleImage = (name = "") => {
  const matchedKey = Object.keys(VEHICLE_IMAGES).find((key) =>
    name.toLowerCase().includes(key.toLowerCase()),
  );

  return matchedKey
    ? VEHICLE_IMAGES[matchedKey]
    : "https://www.packky.com/images/Vechiles/two_wheeler.webp";
};

const getVehicleIcon = (name = "") => {
  const value = name.toLowerCase();

  if (value.includes("truck")) return Truck;
  if (value.includes("4") || value.includes("four")) return Car;
  if (value.includes("3") || value.includes("three")) return Truck;

  return Bike;
};

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

function ChargesAndVechicle() {
  const {
    fetchVehicleDetailsApi,
    fetchDistanceFaresApi,
    createVehicleApi,
    deleteVehicleApi,
    saveDistanceFareApi,
    deleteDistanceFareApi,
    getPackerSlotDetailsApi,
  } = useChargesAndVehicles();

  const [vehicles, setVehicles] = useState([]);
  const [fares, setFares] = useState([]);

  const [loading, setLoading] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [fareSavingId, setFareSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteVehicle, setSelectedDeleteVehicle] = useState(null);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [distanceDeleteModalOpen, setDistanceDeleteModalOpen] = useState(false);
  const [selectedDistanceFare, setSelectedDistanceFare] = useState(null);
  const [deletingDistanceFareId, setDeletingDistanceFareId] = useState(null);
  const [packerSlotGroups, setPackerSlotGroups] = useState([]);
  const [selectedSlotDate, setSelectedSlotDate] = useState("");

  const [vehicleForm, setVehicleForm] = useState({
    name: "",
    description: "",
  });

  const [fareForms, setFareForms] = useState({});
  const { AdminId } = useGlobalContext();

  const normalizePackerSlotGroups = (apiResponse) => {
    const data =
      apiResponse?.data || apiResponse?.response || apiResponse?.result || [];

    const groups = Array.isArray(data) ? data : [];

    return groups.map((group) => ({
      date: String(group?.date || ""),
      slots: Array.isArray(group?.slotDetails)
        ? group.slotDetails.map((slot) => ({
            slotId: String(slot?.pk_slot_id || ""),
            slotTime: String(slot?.slot_time || ""),
            status: String(slot?.status || ""),
            isActive: String(slot?.status || "").toUpperCase() === "Y",
            createdOn: slot?.created_on || "",
            raw: slot,
          }))
        : [],
    }));
  };

  const latestFareByVehicleId = useMemo(() => {
    const map = {};

    fares.forEach((fareGroup) => {
      const vehicleId = String(fareGroup.fk_vehicle_id);

      const distanceList = Array.isArray(fareGroup.distance)
        ? fareGroup.distance
        : [];

      if (distanceList.length === 0) return;

      const latestFare = distanceList.reduce((latest, current) => {
        if (
          !latest ||
          Number(current.pk_distance_fare_id) >
            Number(latest.pk_distance_fare_id)
        ) {
          return current;
        }

        return latest;
      }, null);

      if (latestFare) {
        map[vehicleId] = latestFare;
      }
    });

    return map;
  }, [fares]);

  const distanceFareByVehicleId = useMemo(() => {
    const map = {};

    fares.forEach((fareGroup) => {
      const vehicleId = String(fareGroup.fk_vehicle_id);

      const distanceList = Array.isArray(fareGroup.distance)
        ? [...fareGroup.distance]
        : [];

      map[vehicleId] = distanceList.sort(
        (a, b) =>
          Number(a.minimum_distance || 0) - Number(b.minimum_distance || 0),
      );
    });

    return map;
  }, [fares]);

  const handleEditDistanceSlab = (vehicleId, slab) => {
    setFareForms((prev) => ({
      ...prev,
      [vehicleId]: {
        minimumDistance: String(slab.minimum_distance ?? 0),
        maximumDistance: String(slab.maximum_distance ?? ""),
        fare: String(slab.fare ?? ""),
        rainyWeather: String(slab.rainy_weather_charge ?? ""),
        nightCharge: String(slab.night_charge ?? ""),
        surcharge: String(slab.surcharge ?? ""),
        additionalCharge: String(slab.additional_charge ?? ""),
        pkDistanceFareId: String(slab.pk_distance_fare_id ?? ""),
      },
    }));
  };

  const toggleVehicleFareTable = (vehicleId) => {
    setExpandedVehicleId((prev) =>
      String(prev) === String(vehicleId) ? null : vehicleId,
    );
  };

  const loadPackerSlots = async () => {
    const result = await getPackerSlotDetailsApi({
      userId: String(AdminId),
    });

    const groups = normalizePackerSlotGroups(result);

    setPackerSlotGroups(groups);

    if (!selectedSlotDate && groups[0]?.date) {
      setSelectedSlotDate(groups[0].date);
    }

    return groups;
  };

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);

    await Promise.all([
      fetchVehicleDetailsApi(setVehicles, setLoading, true),
      fetchDistanceFaresApi(setFares, setLoading, true),
      loadPackerSlots(),
    ]);

    if (!silent) setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const nextForms = {};

    vehicles.forEach((vehicle) => {
      const fare = latestFareByVehicleId[String(vehicle.pk_vehicle_id)];

      nextForms[vehicle.pk_vehicle_id] = {
        minimumDistance:
          fare?.minimumDistance !== undefined && fare?.minimumDistance !== null
            ? String(fare.minimumDistance)
            : fare?.minimum_distance !== undefined &&
                fare?.minimum_distance !== null
              ? String(fare.minimum_distance)
              : "0",

        maximumDistance:
          fare?.maximumDistance !== undefined && fare?.maximumDistance !== null
            ? String(fare.maximumDistance)
            : fare?.maximum_distance !== undefined &&
                fare?.maximum_distance !== null
              ? String(fare.maximum_distance)
              : "",

        fare:
          fare?.fare !== undefined && fare?.fare !== null
            ? String(fare.fare)
            : "",

        rainyWeather:
          fare?.rainyWeather !== undefined && fare?.rainyWeather !== null
            ? String(fare.rainyWeather)
            : fare?.rainy_weather_charge !== undefined &&
                fare?.rainy_weather_charge !== null
              ? String(fare.rainy_weather_charge)
              : "",

        nightCharge:
          fare?.nightCharge !== undefined && fare?.nightCharge !== null
            ? String(fare.nightCharge)
            : fare?.night_charge !== undefined && fare?.night_charge !== null
              ? String(fare.night_charge)
              : "",

        surcharge:
          fare?.surcharge !== undefined && fare?.surcharge !== null
            ? String(fare.surcharge)
            : "",

        additionalCharge:
          fare?.additionalCharge !== undefined &&
          fare?.additionalCharge !== null
            ? String(fare.additionalCharge)
            : fare?.additional_charge !== undefined &&
                fare?.additional_charge !== null
              ? String(fare.additional_charge)
              : "",
      };
    });

    setFareForms(nextForms);
  }, [vehicles, latestFareByVehicleId]);

  const handleVehicleInput = (key, value) => {
    setVehicleForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFareInput = (vehicleId, key, value) => {
    setFareForms((prev) => ({
      ...prev,
      [vehicleId]: {
        ...prev[vehicleId],
        [key]: value,
      },
    }));
  };

  const handleAddVehicle = () => {
    if (!vehicleForm.name.trim()) return;

    createVehicleApi(
      {
        name: vehicleForm.name.trim(),
        description: vehicleForm.description.trim(),
      },
      setVehicleSaving,
      () => {
        setVehicleForm({
          name: "",
          description: "",
        });

        loadAll(true);
      },
    );
  };

  const handleDeleteVehicle = (vehicle) => {
    setSelectedDeleteVehicle(vehicle);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;

    setDeleteModalOpen(false);
    setSelectedDeleteVehicle(null);
  };

  const confirmDeleteVehicle = () => {
    if (!selectedDeleteVehicle) return;

    setDeletingId(selectedDeleteVehicle.pk_vehicle_id);

    deleteVehicleApi(selectedDeleteVehicle.pk_vehicle_id, setDeletingId, () => {
      loadAll(true);
      setDeletingId(null);
      setDeleteModalOpen(false);
      setSelectedDeleteVehicle(null);
    });
  };

  const handleDeleteDistanceFare = (vehicle, slab) => {
    setSelectedDistanceFare({
      vehicle,
      slab,
    });

    setDistanceDeleteModalOpen(true);
  };

  const closeDistanceDeleteModal = () => {
    if (deletingDistanceFareId) return;

    setDistanceDeleteModalOpen(false);
    setSelectedDistanceFare(null);
  };

  const confirmDeleteDistanceFare = () => {
    if (!selectedDistanceFare?.slab) return;

    const fareId = selectedDistanceFare.slab.pk_distance_fare_id;

    setDeletingDistanceFareId(fareId);

    deleteDistanceFareApi(
      {
        adminId: String(AdminId),
        id: String(fareId),
      },
      setDeletingDistanceFareId,
      () => {
        loadAll(true);
        setDistanceDeleteModalOpen(false);
        setSelectedDistanceFare(null);
        setDeletingDistanceFareId(null);
      },
    );
  };

  const handleSaveFare = (vehicle) => {
    const form = fareForms[vehicle.pk_vehicle_id] || {};
    const adminId = AdminId;

    if (!form.maximumDistance || !form.fare) return;

    setFareSavingId(vehicle.pk_vehicle_id);

    saveDistanceFareApi(
      {
        adminId: String(adminId),
        vehicleId: String(vehicle.pk_vehicle_id),
        minimumDistance: String(form.minimumDistance || 0),
        maximumDistance: String(form.maximumDistance || 0),
        fare: String(form.fare || 0),
        rainyWeather: String(form.rainyWeather || 0),
        nightCharge: String(form.nightCharge || 0),
        surcharge: String(form.surcharge || 0),
        additionalCharge: String(form.additionalCharge || 0),
      },
      () => {},
      () => {
        loadAll(true);
        setFareSavingId(null);
      },
    );
  };

  return (
    <div className="space-y-6">
      {/* Slim Header */}
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Charges And Vechicle
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure vehicle types and distance based delivery fare.
          </p>
        </div>

        <button
          type="button"
          onClick={() => loadAll()}
          disabled={loading}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {loading ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <RefreshCw size={15} />
          )}
          Refresh
        </button>
      </div>

      {/* Add Vehicle */}
      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Add Vehicle</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Add a new delivery vehicle option.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_1.5fr_auto]">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Vehicle Name
            </label>
            <input
              value={vehicleForm.name}
              onChange={(e) => handleVehicleInput("name", e.target.value)}
              placeholder="Example: 2 Wheeler"
              className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Description
            </label>
            <input
              value={vehicleForm.description}
              onChange={(e) =>
                handleVehicleInput("description", e.target.value)
              }
              placeholder="Optional"
              className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={handleAddVehicle}
              disabled={vehicleSaving || !vehicleForm.name.trim()}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-orange-500 px-4 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
            >
              {vehicleSaving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              Add
            </button>
          </div>
        </div>
      </section>

      {/* Vehicle Fare List */}
      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Vehicle Fare Configuration
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Set per km fare and minimum delivery charge for each vehicle.
            </p>
          </div>

          <div className="hidden text-xs text-gray-500 md:block">
            {vehicles.length} vehicles · {fares.length} fare rules
          </div>
        </div>

        {loading ? (
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="grid animate-pulse grid-cols-1 gap-4 px-4 py-4 md:grid-cols-[260px_1fr_1fr_120px]"
              >
                <div className="h-10 rounded bg-gray-100" />
                <div className="h-10 rounded bg-gray-100" />
                <div className="h-10 rounded bg-gray-100" />
                <div className="h-10 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-gray-700">
              No vehicle added yet
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Add vehicle first, then configure fare.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {vehicles.map((vehicle) => {
              const VehicleIcon = getVehicleIcon(vehicle.name);
              const currentFare =
                latestFareByVehicleId[String(vehicle.pk_vehicle_id)];
              const form = fareForms[vehicle.pk_vehicle_id] || {};
              const slabs =
                distanceFareByVehicleId[String(vehicle.pk_vehicle_id)] || [];
              const isExpanded =
                String(expandedVehicleId) === String(vehicle.pk_vehicle_id);
              const isFareSaving = fareSavingId === vehicle.pk_vehicle_id;
              const isDeleting = deletingId === vehicle.pk_vehicle_id;

              return (
                <React.Fragment key={vehicle.pk_vehicle_id}>
                  <div
                    key={vehicle.pk_vehicle_id}
                    className="grid grid-cols-1 gap-4 px-4 py-4 transition hover:bg-gray-50 md:grid-cols-[280px_1fr_1fr_130px]"
                  >
                    {/* Vehicle Info */}
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          toggleVehicleFareTable(vehicle.pk_vehicle_id)
                        }
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600"
                        title={
                          isExpanded
                            ? "Hide distance slabs"
                            : "Show distance slabs"
                        }
                      >
                        <ChevronDown
                          size={17}
                          className={`transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50">
                        <img
                          src={getVehicleImage(vehicle.name)}
                          alt={vehicle.name}
                          className="h-10 w-10 object-contain"
                        />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <VehicleIcon size={14} className="text-orange-500" />
                          <p className="truncate text-sm font-semibold text-gray-900">
                            {vehicle.name}
                          </p>
                        </div>

                        <p className="mt-0.5 truncate text-xs text-gray-500">
                          ID: {vehicle.pk_vehicle_id}
                          {vehicle.description
                            ? ` · ${vehicle.description}`
                            : " · No description"}
                        </p>

                        {currentFare && (
                          <p className="mt-1 text-xs text-gray-500">
                            Current slab:{" "}
                            <span className="font-medium text-gray-800">
                              {currentFare.minimumDistance ??
                                currentFare.minimum_distance ??
                                0}
                              km -{" "}
                              {currentFare.maximumDistance ??
                                currentFare.maximum_distance ??
                                0}
                              km
                            </span>{" "}
                            · Fare{" "}
                            <span className="font-medium text-gray-800">
                              ₹{formatAmount(currentFare.fare)}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Minimum Distance
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.minimumDistance || ""}
                          onChange={(e) =>
                            handleFareInput(
                              vehicle.pk_vehicle_id,
                              "minimumDistance",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Maximum Distance
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.maximumDistance || ""}
                          onChange={(e) =>
                            handleFareInput(
                              vehicle.pk_vehicle_id,
                              "maximumDistance",
                              e.target.value,
                            )
                          }
                          placeholder="2"
                          className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Base Fare
                        </label>
                        <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
                          <IndianRupee size={13} className="text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            value={form.fare || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicle.pk_vehicle_id,
                                "fare",
                                e.target.value,
                              )
                            }
                            placeholder="20.4"
                            className="ml-1 w-full bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Rainy Weather
                        </label>
                        <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
                          <IndianRupee size={13} className="text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            value={form.rainyWeather || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicle.pk_vehicle_id,
                                "rainyWeather",
                                e.target.value,
                              )
                            }
                            placeholder="41.2"
                            className="ml-1 w-full bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Night Charge
                        </label>
                        <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
                          <IndianRupee size={13} className="text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            value={form.nightCharge || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicle.pk_vehicle_id,
                                "nightCharge",
                                e.target.value,
                              )
                            }
                            placeholder="78.25"
                            className="ml-1 w-full bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Surcharge
                        </label>
                        <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
                          <IndianRupee size={13} className="text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            value={form.surcharge || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicle.pk_vehicle_id,
                                "surcharge",
                                e.target.value,
                              )
                            }
                            placeholder="20"
                            className="ml-1 w-full bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Additional Charge
                        </label>
                        <div className="flex h-9 items-center rounded-md border border-gray-300 bg-white px-2 focus-within:border-orange-400 focus-within:ring-1 focus-within:ring-orange-400">
                          <IndianRupee size={13} className="text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            value={form.additionalCharge || ""}
                            onChange={(e) =>
                              handleFareInput(
                                vehicle.pk_vehicle_id,
                                "additionalCharge",
                                e.target.value,
                              )
                            }
                            placeholder="36"
                            className="ml-1 w-full bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-end gap-2 md:justify-end">
                      <button
                        type="button"
                        onClick={() => handleSaveFare(vehicle)}
                        disabled={
                          isFareSaving || !form.maximumDistance || !form.fare
                        }
                        className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-orange-500 px-3 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 md:flex-none"
                      >
                        {isFareSaving ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Save size={14} />
                        )}
                        Save
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteVehicle(vehicle)}
                        disabled={isDeleting}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-60"
                      >
                        {isDeleting ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mx-4 mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                        <div>
                          <p className="text-xs font-semibold text-gray-900">
                            Existing Distance Slabs
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {slabs.length} fare rules configured for this
                            vehicle
                          </p>
                        </div>

                        <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                          {slabs.length} slabs
                        </span>
                      </div>

                      {slabs.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-gray-500">
                          No distance slab added yet.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-xs">
                            <thead className="bg-gray-50 text-gray-500">
                              <tr>
                                <th className="px-3 py-2 font-medium">
                                  Distance
                                </th>
                                <th className="px-3 py-2 font-medium">Fare</th>
                                <th className="px-3 py-2 font-medium">Rainy</th>
                                <th className="px-3 py-2 font-medium">Night</th>
                                <th className="px-3 py-2 font-medium">
                                  Surcharge
                                </th>
                                <th className="px-3 py-2 font-medium">
                                  Additional
                                </th>
                                <th className="px-3 py-2 text-right font-medium">
                                  Action
                                </th>
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                              {slabs.map((slab) => {
                                const isEditing =
                                  String(form.pkDistanceFareId || "") ===
                                  String(slab.pk_distance_fare_id || "");

                                return (
                                  <tr
                                    key={slab.pk_distance_fare_id}
                                    className={`transition hover:bg-gray-50 ${
                                      isEditing ? "bg-orange-50/70" : "bg-white"
                                    }`}
                                  >
                                    <td className="px-3 py-2 font-medium text-gray-900">
                                      {formatAmount(slab.minimum_distance)} km -{" "}
                                      {formatAmount(slab.maximum_distance)} km
                                    </td>

                                    <td className="px-3 py-2 text-gray-700">
                                      ₹{formatAmount(slab.fare)}
                                    </td>

                                    <td className="px-3 py-2 text-gray-700">
                                      ₹{formatAmount(slab.rainy_weather_charge)}
                                    </td>

                                    <td className="px-3 py-2 text-gray-700">
                                      ₹{formatAmount(slab.night_charge)}
                                    </td>

                                    <td className="px-3 py-2 text-gray-700">
                                      ₹{formatAmount(slab.surcharge)}
                                    </td>

                                    <td className="px-3 py-2 text-gray-700">
                                      ₹{formatAmount(slab.additional_charge)}
                                    </td>

                                    <td className="px-3 py-2 text-right">
                                      <div className="flex justify-end gap-2">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleEditDistanceSlab(
                                              vehicle.pk_vehicle_id,
                                              slab,
                                            )
                                          }
                                          className="rounded-md border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100"
                                        >
                                          {isEditing ? "Editing" : "Edit"}
                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleDeleteDistanceFare(
                                              vehicle,
                                              slab,
                                            )
                                          }
                                          disabled={
                                            String(deletingDistanceFareId) ===
                                            String(slab.pk_distance_fare_id)
                                          }
                                          className="rounded-md border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          {String(deletingDistanceFareId) ===
                                          String(slab.pk_distance_fare_id)
                                            ? "Deleting"
                                            : "Delete"}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </section>

      {/* delete selected vehicle */}

      {deleteModalOpen && selectedDeleteVehicle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeDeleteModal}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 size={22} />
              </div>

              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  Delete vehicle?
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900">
                    {selectedDeleteVehicle.name}
                  </span>
                  ? This vehicle will be removed from your settings.
                </p>

                <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  This action may also affect fare configuration linked with
                  this vehicle.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={!!deletingId}
                className="inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteVehicle}
                disabled={!!deletingId}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete selected distance fare */}

      {distanceDeleteModalOpen && selectedDistanceFare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeDistanceDeleteModal}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 size={22} />
              </div>

              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  Delete distance fare?
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete this distance fare slab for{" "}
                  <span className="font-semibold text-gray-900">
                    {selectedDistanceFare.vehicle?.name}
                  </span>
                  ?
                </p>

                <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 text-xs text-gray-700">
                  <div className="flex justify-between">
                    <span>Distance</span>
                    <span className="font-semibold">
                      {formatAmount(
                        selectedDistanceFare.slab?.minimum_distance,
                      )}{" "}
                      km -{" "}
                      {formatAmount(
                        selectedDistanceFare.slab?.maximum_distance,
                      )}{" "}
                      km
                    </span>
                  </div>

                  <div className="mt-1 flex justify-between">
                    <span>Fare</span>
                    <span className="font-semibold">
                      ₹{formatAmount(selectedDistanceFare.slab?.fare)}
                    </span>
                  </div>
                </div>

                <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  This will delete only this selected distance slab, not the
                  vehicle.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDistanceDeleteModal}
                disabled={!!deletingDistanceFareId}
                className="inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteDistanceFare}
                disabled={!!deletingDistanceFareId}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingDistanceFareId ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    Delete Slab
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChargesAndVechicle;
